/**
 * Labelled controls. Every one wires its own `id`/`htmlFor` pair and its own `aria-describedby`,
 * so a caller cannot ship a field whose hint is invisible to a screen reader.
 *
 * What these validate is what a form control can validate on its own — required, a character
 * class, bounds the server supplied. Anything that could produce a wrong date is asked of the
 * server (§6.2).
 */

import {
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  cloneElement,
} from 'react';
import styles from './Field.module.css';

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  /** Flex basis override, for a row of fields the layout hand-tunes. */
  grow?: number;
  className?: string;
  children: ReactElement<{ id?: string; 'aria-describedby'?: string }>;
}

export function Field({ label, hint, grow, className, children }: FieldProps) {
  const generated = useId();
  const id = children.props.id ?? generated;
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div
      className={[styles.field, className].filter(Boolean).join(' ')}
      style={grow === undefined ? undefined : { flexGrow: grow }}
    >
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {cloneElement(children, {
        id,
        ...(hintId ? { 'aria-describedby': hintId } : {}),
      })}
      {hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function TextInput({ invalid, className, ...rest }: TextInputProps) {
  return (
    <input
      {...rest}
      type={rest.type ?? 'text'}
      aria-invalid={invalid || undefined}
      className={[styles.input, invalid ? styles.invalid : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export interface DateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** Server-supplied bounds. The client does not decide what the window is. */
  min?: string;
  max?: string;
  invalid?: boolean;
}

export function DateInput({ value, onChange, invalid, className, ...rest }: DateInputProps) {
  return (
    <input
      {...rest}
      type="date"
      value={value}
      aria-invalid={invalid || undefined}
      onChange={(event) => onChange(event.currentTarget.value)}
      className={[styles.input, styles.date, invalid ? styles.invalid : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: number | '';
  onChange: (value: number) => void;
  invalid?: boolean;
}

export function NumberInput({ value, onChange, invalid, className, ...rest }: NumberInputProps) {
  return (
    <input
      {...rest}
      type="number"
      inputMode="numeric"
      value={value}
      aria-invalid={invalid || undefined}
      onChange={(event) => onChange(Number(event.currentTarget.value))}
      className={[styles.input, invalid ? styles.invalid : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export interface UrlInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}

/** Never normalises on change — normalisation is the server's, on submit. */
export function UrlInput({ value, onChange, invalid, className, ...rest }: UrlInputProps) {
  return (
    <input
      {...rest}
      type="url"
      inputMode="url"
      spellCheck={false}
      value={value}
      aria-invalid={invalid || undefined}
      onChange={(event) => onChange(event.currentTarget.value)}
      className={[styles.input, invalid ? styles.invalid : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  className,
  ...rest
}: SelectProps<T>) {
  return (
    <select
      {...rest}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value as T)}
      className={[styles.select, className].filter(Boolean).join(' ')}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  monospace?: boolean;
}

export function Textarea({ monospace, className, ...rest }: TextareaProps) {
  return (
    <textarea
      {...rest}
      className={[styles.textarea, monospace ? styles.monospace : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export interface PeriodValue<U extends string = string> {
  value: number;
  unit: U;
}

export interface PeriodInputProps<U extends string = string> {
  value: PeriodValue<U>;
  onChange: (value: PeriodValue<U>) => void;
  /** The unit vocabulary from GET /meta. No unit is hardcoded here. */
  units: ReadonlyArray<SelectOption<U>>;
  id?: string;
  'aria-describedby'?: string;
  min?: number;
}

/**
 * A number and a unit, never a bare month count. The prompt asks the model for the unit the
 * provision uses, and "twenty working days" must be recordable as twenty days rather than
 * silently as a month.
 */
export function PeriodInput<U extends string>({
  value,
  onChange,
  units,
  id,
  min = 1,
  ...rest
}: PeriodInputProps<U>) {
  return (
    <div className={styles.period}>
      <NumberInput
        id={id}
        className={styles.periodValue}
        value={value.value}
        min={min}
        aria-label="Value"
        aria-describedby={rest['aria-describedby']}
        onChange={(next) => onChange({ ...value, value: next })}
      />
      <Select
        className={styles.periodUnit}
        value={value.unit}
        options={units}
        aria-label="Unit"
        onChange={(next) => onChange({ ...value, unit: next })}
      />
    </div>
  );
}

export interface SegmentedToggleProps<T extends string> {
  options: ReadonlyArray<SelectOption<T>>;
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
}

/**
 * A radio group with a roving tabindex, so arrow keys work. The prototype's `aria-pressed`
 * buttons are defensible for two options and give nothing for three.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ...rest
}: SegmentedToggleProps<T>) {
  const move = (delta: number) => {
    const at = options.findIndex((option) => option.value === value);
    const next = options[(at + delta + options.length) % options.length];
    if (next) onChange(next.value);
  };

  return (
    <div className={styles.toggle} role="radiogroup" aria-label={rest['aria-label']}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className={styles.toggleButton}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                move(1);
              }
              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(-1);
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

/**
 * A controlled input keeps focus and the caret without help. The prototype re-focuses and forces
 * the caret to the end on every keystroke, which breaks editing mid-string; that must not be
 * ported, and this component exists partly to say so.
 */
export function SearchInput({ value, onChange, className, ...rest }: SearchInputProps) {
  return (
    <input
      {...rest}
      type="search"
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      className={[styles.input, styles.search, className].filter(Boolean).join(' ')}
    />
  );
}
